// This is to integrate global button styles for wordpress rich text editor updates.

import { css } from 'styled-components'
import borders from './borders'
import color from './color'
import font from './font'
import space from './space'

const buttonCommonStyle = css`
  width: auto;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${space.xs};
  appearance: none;
  font-family: ${font.family.text};
  font-weight: ${font.weight.bold};
  line-height: 1;
  white-space: nowrap;
  border-radius: ${borders.sizes.xl};
  transition: all 0.3s ease;
  margin: ${space.sm} 0;

  &:disabled {
    cursor: disabled;
    pointer-events: none;
    opacity: 0.4;
  }

  &:hover,
  &:focus {
    text-decoration: none;
  }
`

// Lighten a hex color by a percentage
const lighten = (hex: string, percent: number) => {
  const cleanHex = hex.replace('#', '')
  const r = parseInt(cleanHex.substring(0, 2), 16)
  const g = parseInt(cleanHex.substring(2, 4), 16)
  const b = parseInt(cleanHex.substring(4, 6), 16)

  const newR = Math.min(255, Math.floor(r + (255 - r) * (percent / 100)))
  const newG = Math.min(255, Math.floor(g + (255 - g) * (percent / 100)))
  const newB = Math.min(255, Math.floor(b + (255 - b) * (percent / 100)))

  return `rgb(${newR}, ${newG}, ${newB})`
}

// Helper: check brightness of color
const getContrastColor = (hex: string) => {
  // remove '#' if present
  const cleanHex = hex.replace('#', '')
  // parse rgb
  const r = parseInt(cleanHex.substr(0, 2), 16)
  const g = parseInt(cleanHex.substr(2, 2), 16)
  const b = parseInt(cleanHex.substr(4, 2), 16)

  // luminance formula
  const brightness = (r * 299 + g * 587 + b * 114) / 1000

  return brightness > 150 ? '#000' : '#fff'
}

export const buttonColorClasses = Object.entries(color.generalButtonStyle).map(([key, value]) => {
  const colorValue = value as string
  const hoverColor = lighten(colorValue, 20) // lighten by 20%
  return css`
    .button-${key} {
      ${buttonCommonStyle}
      border: none;
      text-decoration: none;
      &:hover {
        text-decoration: none;
      }
      background: ${colorValue};
      color: ${getContrastColor(colorValue)} !important;

      &:hover {
        background: ${hoverColor}; /* ✅ actually apply lighter background */
        color: ${getContrastColor(hoverColor)} !important;
      }
    }

    a.button-${key} {
      display: inline-flex;
      &:hover {
        background: ${hoverColor}; /* ✅ actually apply lighter background */
        color: ${getContrastColor(hoverColor)} !important;
      }
    }
  `
})

export const buttonSmallGeneralStyle = css`
  .button-small {
    height: ${space.xl2};
    padding: 0 ${space.md};
    font-size: ${font.size.xsmall};

    .icon {
      font-size: ${font.size.root};
    }

    &-pill {
      padding: 0 ${space.xs};
      border-radius: ${borders.radius.pill};
    }
  }
`
export const buttonMediumGeneralStyle = css`
  .button-medium {
    height: ${space.xl3};
    padding: 0 ${space.lg};
    font-size: ${font.size.small};

    .icon {
      font-size: ${font.size.h5};
    }

    &-pill {
      padding: 0 ${space.sm};
      border-radius: ${borders.radius.pill};
    }
  }
`

export const buttonLargeGeneralStyle = css`
  .button-large {
    height: ${space.xl4};
    padding: 0 ${space.xl};
    font-size: ${font.size.root};

    .icon {
      font-size: ${font.size.h4};
    }
    &-pill {
      padding: 0 ${space.md};
      border-radius: ${borders.radius.pill};
    }
  }
`
export const buttonBlock = css`
  .button-block {
    width: 100%;
  }
`
